import {Component, HostListener, OnDestroy, effect, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {ImageCropperComponent, ImageCroppedEvent} from 'ngx-image-cropper';
import {ONBOARDING_WELCOME_VIDEO_HASH, ONBOARDING_WELCOME_VIDEO_ID, TourService} from '../../../services/tour.service';
import {ApiService} from '../../../services/api.service';
import {KeycloakService} from '../../../services/keycloak.service';
import {User} from '../../../interfaces/api-interfaces';

@Component({
    selector: 'app-tour-overlay',
    standalone: true,
    imports: [CommonModule, FormsModule, ImageCropperComponent],
    templateUrl: './tour-overlay.component.html',
})
export class TourOverlayComponent implements OnDestroy {
    protected readonly tourService = inject(TourService);
    private readonly apiService = inject(ApiService);
    private readonly keycloakService = inject(KeycloakService);
    private readonly sanitizer = inject(DomSanitizer);

    protected readonly welcomeVideoUrl: SafeResourceUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://player.vimeo.com/video/${ONBOARDING_WELCOME_VIDEO_ID}?h=${ONBOARDING_WELCOME_VIDEO_HASH}`
        );
    protected readonly welcomeTimerElapsed = signal(false);
    protected readonly saving = signal(false);

    // Phase 0.2 — avatar upload state (mirrors pages/settings/profile behaviour)
    protected avatarCropperFile: File | null = null;
    protected avatarCroppedBase64 = '';
    protected avatarPreviewUrl: string | null = null;

    // Phase 0.3 — bio state
    protected bioText = '';

    private welcomeTimer?: ReturnType<typeof setTimeout>;
    private readonly onScrollCapture = () => this.tourService.refreshTargetRect();

    constructor() {
        effect(() => {
            const step = this.tourService.activeStep();
            if (step?.id === 'p0-welcome') {
                this.welcomeTimerElapsed.set(false);
                clearTimeout(this.welcomeTimer);
                this.welcomeTimer = setTimeout(() => this.welcomeTimerElapsed.set(true), (step.minDelaySeconds ?? 5) * 1000);
            }
            if (step?.id === 'p0-bio') {
                this.bioText = this.keycloakService.currentUser()?.biography || '';
            }
            if (step?.id === 'p0-avatar') {
                this.avatarPreviewUrl = this.keycloakService.currentUser()?.profileImage || null;
            }
        });

        // The dashboard scrolls inside a nested container (app.html), not the window,
        // and 'scroll' doesn't bubble — a capture-phase document listener is the only
        // way to catch it regardless of which ancestor is the actual scroller.
        document.addEventListener('scroll', this.onScrollCapture, {capture: true, passive: true});
    }

    ngOnDestroy() {
        document.removeEventListener('scroll', this.onScrollCapture, {capture: true});
        clearTimeout(this.welcomeTimer);
    }

    @HostListener('window:resize')
    onViewportResize() {
        this.tourService.refreshTargetRect();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        this.tourService.handleGlobalClick(event.target);
    }

    canAdvanceFullscreen(): boolean {
        const step = this.tourService.activeStep();
        if (!step) return false;
        if (step.id === 'p0-welcome') return this.welcomeTimerElapsed();
        return !this.saving();
    }

    onFullscreenPrimary() {
        const step = this.tourService.activeStep();
        if (!step) return;
        if (step.id === 'p0-avatar') {
            this.saveAvatarAndAdvance();
        } else if (step.id === 'p0-bio') {
            this.saveBioAndAdvance();
        } else {
            this.tourService.advance();
        }
    }

    onAvatarFileSelected(event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
            this.avatarCropperFile = file;
            this.avatarCroppedBase64 = '';
        }
    }

    onAvatarCropped(event: ImageCroppedEvent) {
        this.avatarCroppedBase64 = event.base64 || '';
    }

    applyAvatarCrop() {
        if (this.avatarCroppedBase64) {
            this.avatarPreviewUrl = this.avatarCroppedBase64;
        }
        this.avatarCropperFile = null;
        this.avatarCroppedBase64 = '';
    }

    cancelAvatarCrop() {
        this.avatarCropperFile = null;
        this.avatarCroppedBase64 = '';
    }

    private saveAvatarAndAdvance() {
        if (!this.avatarPreviewUrl) {
            this.tourService.advance();
            return;
        }
        this.saving.set(true);
        this.apiService.updateUserProfile({profileImage: this.avatarPreviewUrl} as Partial<User> as User).subscribe({
            next: (updatedUser) => {
                this.keycloakService.updateUser(updatedUser);
                this.saving.set(false);
                this.tourService.advance();
            },
            error: (err) => {
                console.error('Failed to save onboarding profile image:', err);
                this.saving.set(false);
                this.tourService.advance();
            },
        });
    }

    private saveBioAndAdvance() {
        const biography = this.bioText.trim().slice(0, 500);
        if (!biography) {
            this.tourService.advance();
            return;
        }
        this.saving.set(true);
        this.apiService.updateUserProfile({biography} as Partial<User> as User).subscribe({
            next: (updatedUser) => {
                this.keycloakService.updateUser(updatedUser);
                this.saving.set(false);
                this.tourService.advance();
            },
            error: (err) => {
                console.error('Failed to save onboarding biography:', err);
                this.saving.set(false);
                this.tourService.advance();
            },
        });
    }

    bubbleStyle(rect: DOMRect): Record<string, string> {
        const margin = 12;
        const width = 320;
        let top = rect.bottom + margin;
        if (top + 180 > window.innerHeight) {
            top = Math.max(8, rect.top - margin - 180);
        }
        let left = rect.left;
        if (left + width > window.innerWidth - 8) {
            left = window.innerWidth - width - 8;
        }
        if (left < 8) left = 8;
        return {top: `${top}px`, left: `${left}px`, width: `${width}px`};
    }

    ringStyle(rect: DOMRect): Record<string, string> {
        const pad = 4;
        return {
            top: `${rect.top - pad}px`,
            left: `${rect.left - pad}px`,
            width: `${rect.width + pad * 2}px`,
            height: `${rect.height + pad * 2}px`,
        };
    }

    backdropRects(rect: DOMRect): Record<string, string>[] {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        return [
            {top: '0', left: '0', width: '100%', height: `${Math.max(0, rect.top)}px`},
            {top: `${rect.bottom}px`, left: '0', width: '100%', height: `${Math.max(0, vh - rect.bottom)}px`},
            {top: `${rect.top}px`, left: '0', width: `${Math.max(0, rect.left)}px`, height: `${rect.height}px`},
            {top: `${rect.top}px`, left: `${rect.right}px`, width: `${Math.max(0, vw - rect.right)}px`, height: `${rect.height}px`},
        ];
    }
}
